import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { publicGuard } from './core/guards/public.guard';
import { lendeeCallFlagsGuard } from './pages/public/credee-demo/lendee-call-flags.guard';
import { PrivateLayoutComponent } from './layout/private-layout/private-layout.component';
import { PublicLayoutComponent } from './layout/public-layout/public-layout.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'unpaid-payments',
    redirectTo: 'app/prism/unpaid-payments',
    pathMatch: 'full',
  },

  {
    path: 'chatbot',
    children: [
      {
        path: 'embedded',
        loadComponent: () =>
          import('./pages/public/chatbot/embedded-chatbot/embedded-chatbot.component').then(
            (m) => m.EmbeddedChatbotComponent
          ),
      },
      {
        path: 'chat-interface',
        loadComponent: () =>
          import('./pages/public/chatbot/chat-interface/chat-interface.component').then(
            (m) => m.ChatInterfaceComponent
          ),
      }
    ]
  },

  {
    path: 'embed',
    children: [
      {
        path: 'chat',
        loadComponent: () =>
          import('./pages/public/embed/chatbot-builder/chat.component').then(
            (m) => m.ChatbotBuilderChatComponent
          ),
      }
    ]
  },
  {
    path: 'audit-page',
    loadComponent: () =>
      import('./pages/public/audit-page/audit-page.component').then(
        (m) => m.AuditPageComponent
      ),
  },
  {
    path: 'crm-panel',
    loadComponent: () =>
      import('./pages/public/crm-panel/crm-panel.component').then(
        (m) => m.CrmPanelComponent
      ),
  },
  {
    // Embeddable Credee AI Sales Agent demo. Public + iframe-friendly; gated on
    // a `?token=` query param. Split into a setup screen (`/credee-demo`) and
    // the real live call (`/credee-demo/call`).
    path: 'instant-demo',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/public/credee-demo/credee-demo-setup.component').then(
            (m) => m.CredeeDemoSetupComponent
          ),
      },
      {
        path: 'call',
        // Lendee only: plants the call-start barge flags the turn-control plane
        // reads from the URL (see lendee-call-flags.guard.ts). Inert elsewhere.
        canActivate: [lendeeCallFlagsGuard],
        loadComponent: () => import('./pages/public/credee-demo/credee-demo-call.component').then(
          (m) => m.CredeeDemoCallComponent
        ),
      },
      {
        path: 'preferences',
        loadComponent: () => import('./pages/public/credee-demo/credee-demo-preferences.component').then(
          (m) => m.CredeeDemoPreferencesComponent
        ),
      },
      {
        // Bridge for a Prism agent who ACCEPTED an "Ask to Agent" invite:
        // /credee-demo/agent-join?sid=<roomSid>&handoff=<nonce>. It plants the pre-approved
        // guest identity, then forwards into `call` — so the agent enters without the host
        // approving them. See credee-agent-join.contract.ts.
        path: 'agent-join',
        loadComponent: () =>
          import('./pages/public/credee-demo/credee-agent-join.component').then(
            (m) => m.CredeeAgentJoinComponent
          ),
      },
      {
        // "What got in the way?" drop-off feedback page, linked from the
        // early/midway nudge email: /credee-demo/credee-feedback?businessId=<id>
        path: 'credee-feedback',
        loadComponent: () =>
          import('./pages/public/credee-demo/credee-feedback-page.component').then(
            (m) => m.CredeeFeedbackPageComponent
          ),
      },
    ],
  },
  {
    // LiveKit-based "AI Sales Engineer" demo, backed by the standalone
    // prism-backend-python/livecall_agent service (FastAPI + LiveKit agent
    // worker). Public, self-contained; the browser joins the LiveKit room
    // directly via livekit-client. Setup screen at `/instant-demo-v2`,
    // live room at `/instant-demo-v2/call`. The setup page requires a
    // `?token=<product-slug>` query param — links without it are refused.
    path: 'instant-demo-v2',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/public/credee-demo-livekit/livekit-demo-setup.component').then(
            (m) => m.LivekitDemoSetupComponent
          ),
      },
      {
        // v2 room design ("Credee AI Demo" white theme). The component
        // EXTENDS LivekitDemoCallComponent, so every join/host/guest/observer
        // flow and query param (?room, ?product/token, ?name, ?observer,
        // ?handoff, ?repHandoff) behaves exactly as before.
        path: 'call',
        loadComponent: () =>
          import('./pages/public/credee-demo-livekit/livekit-demo-call-v2.component').then(
            (m) => m.LivekitDemoCallV2Component
          ),
      },
      {
        // Egress recorder template: headless Chrome opens this with
        // ?url=<ws>&token=<jwt>; it joins hidden and renders slide + tiles
        // for the room-composite recording. Not linked from any UI.
        path: 'record',
        loadComponent: () =>
          import('./pages/public/credee-demo-livekit/livekit-recorder.component').then(
            (m) => m.LivekitRecorderComponent
          ),
      },
    ],
  },
  {
    // Replays a recorded credee-demo call: DOM session (rrweb) + synced audio,
    // + webcam video when the session has one. Public; URL-driven:
    // /credee-demo/viewer?src=<url-encoded session-dom.json.gz S3 URL>
    path: 'credee-demo/viewer',
    loadComponent: () =>
      import('./pages/public/credee-demo/viewer/viewer.component').then(
        (m) => m.ReplayViewerComponent
      ),
  },
  {
    // Replays a recorded credee-demo call: DOM session (rrweb) + synced audio,
    // + webcam video when the session has one. Public; URL-driven:
    // /credee-demo/viewer?src=<url-encoded session-dom.json.gz S3 URL>
    path: 'instant-demo/viewer',
    loadComponent: () =>
      import('./pages/public/credee-demo/viewer/viewer.component').then(
        (m) => m.ReplayViewerComponent
      ),
  },
  // {
  //   path: 'extension-download',
  //   loadComponent: () =>
  //     import('./pages/public/extension-download/extension-download.component').then(
  //       (m) => m.ExtensionDownloadComponent
  //     ),
  // },
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./pages/public/privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent
      ),
  },
  {
    path: 'extension/privacy-policy',
    loadComponent: () =>
      import(
        './pages/public/master-extension-privacy-policy/master-extension-privacy-policy.component'
      ).then((m) => m.MasterExtensionPrivacyPolicyComponent),
  },
  // Public routes (for non-authenticated users)
  {
    path: '',
    component: PublicLayoutComponent,
    canActivate: [publicGuard],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./pages/public/login/login.component').then(
            (m) => m.LoginComponent
            //import('./pages/public/chatbot/chatbot.component').then(
            //  (m) => m.ChatbotComponent
          ),
      },
      //   {
      //   path: 'chatbot',
      //   loadComponent: () =>
      //      import('./pages/public/chatbot/chatbot.component').then(
      //         (m) => m.ChatbotComponent
      //      ),
      // },
      {
        path: 'verify',
        loadComponent: () =>
          import(
            './pages/public/verification-code/verification-code.component'
          ).then((m) => m.VerificationCodeComponent),
      },
      {
        path: 'password-setup',
        loadComponent: () =>
          import('./pages/public/password-setup/password-setup.component').then(
            (m) => m.PasswordSetupComponent
          ),
      },
      {
        path: 'request-access',
        loadComponent: () =>
          import('./pages/public/request-access/request-access.component').then(
            (m) => m.RequestAccessComponent
          ),
      },
    ],
  },
  // Protected routes (for authenticated users)
  {
    path: 'app',
    component: PrivateLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'denefits',
      },
      {
        path: 'home/profile',
        loadComponent: () =>
          import('./pages/private/profile/profile.component').then(
            (m) => m.UserProfileComponent
          ),
        title: 'Profile',
      },
      {
        path: 'schedule-monitor',
        loadComponent: () =>
          import('./pages/private/schedule-monitor/schedule-monitor.component').then(
            (m) => m.ScheduleMonitorComponent
          ),
      },
      {
        path: '**',
        loadComponent: () =>
          import('./pages/private/page-not-found/page-not-found.component').then(
            (m) => m.PageNotFoundComponent
          ),
      },

      // 🔥 ONLY dynamic routes
    ],
  },
];
